import React, { useContext, useState, useEffect, useCallback } from 'react';

function isCallable<T>(f: T | ((prev: T | undefined) => T) | ((prev: T | undefined) => Promise<T>))
    : f is ((prev: T | undefined) => T) | ((prev: T | undefined) => Promise<T>) {
    if (typeof f === 'function' || (typeof f === 'object' && f !== null) && 'call' in f) return true;
    return false;
}

class Store<T> {
    constructor(public value: T | undefined = undefined, readonly identifier?: string) { }

    updating = false;

    private listeners = new Set<(value: T) => void>();
    addListener(listener: (value: T) => void) { this.listeners.add(listener); }
    removeListener(listener: (value: T) => void) { this.listeners.delete(listener); }

    async update(valueOrUpdate: T | ((prev: T | undefined) => T) | ((prev: T | undefined) => Promise<T>)) {
        if (this.updating) return;

        if (isCallable(valueOrUpdate)) {
            try {
                this.updating = true;
                const res = valueOrUpdate(this.value);
                if (typeof res === 'object' && res !== null && 'then' in res) {
                    this.set(await res);
                } else {
                    this.set(res);
                }
            } finally {
                this.updating = false;
            }
        } else {
            this.set(valueOrUpdate);
        }
    }

    set(value: T) {
        this.value = value;
        this.listeners.forEach(listener => listener(value));
    }
}

export function createContextStore<T>(identifier?: string) {
    const store = new Store<T>(undefined, identifier);
    const Context = React.createContext(store);

    function withStore(children: React.ReactNode) {
        return (
            <Context.Provider value={store}>
                {children}
            </Context.Provider>
        );
    }

    function useStoredValue() {
        const holder = useContext(Context);
        const [value, set] = useState(holder.value);
        useEffect(() => {
            const listener = (newValue: T) => {
                set(newValue);
            };
            holder.addListener(listener);
            return () => holder.removeListener(listener);
        }, [holder]);
        return value;
    }

    function useStoreValue() {
        const holder = useContext(Context);
        return useCallback((valueOrUpdate: T | ((prev: T | undefined) => T) | ((prev: T | undefined) => Promise<T>)) => {
            holder.update(valueOrUpdate);
        }, [holder]);
    }

    return {
        withStore,
        useStoredValue,
        useStoreValue,
    };
}
